import { Box, Checkbox, Group, ScrollArea, Text, UnstyledButton } from "@mantine/core";
import { IconAlertTriangle, IconRefresh } from "@tabler/icons-react";
import { Badge, Button, spacing } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type { ReviewImageSummary } from "@models/review";
import { imageKey } from "./imageKey";
import "./FailedSection.scss";

interface FailedSectionProps {
  /** The images that ended FAILED (any reason). */
  failed: ReviewImageSummary[];
  /** The selected regenerable images, keyed by `requestId/objId`. */
  selected: ReadonlySet<string>;
  /** Whether the current user may regenerate (`generation.admin`). */
  canRegenerate: boolean;
  /** Whether the run is GENERATED (parked for review) — regenerate needs this. */
  runParked: boolean;
  /** Whether a regenerate request is in flight. */
  submitting: boolean;
  /** The id (`requestId/objId`) of the active (open) failed image, if any. */
  activeKey: string | null;
  onToggle: (key: string) => void;
  onToggleAll: () => void;
  onSelectImage: (image: ReviewImageSummary) => void;
  onRegenerate: () => void;
}

/**
 * The "Failed to generate (n)" section that sits below the review list in the
 * sidebar (AB#955377, F9/D17). It lists every FAILED image with a reason chip;
 * a checkbox appears ONLY for regenerable ones (FAILED / generation_failed on an
 * GENERATED run) — a deterministic failure (source_unreadable, policy_skip)
 * shows the chip and no checkbox. A "Select all" toggle covers the regenerable
 * rows, and a "Regenerate selected (n)" button is shown ONLY to a
 * `generation.admin` user, disabled when nothing is ticked or the run is not
 * parked. Clicking any failed row opens its read-only view in the main area.
 */
export function FailedSection({
  failed,
  selected,
  canRegenerate,
  runParked,
  submitting,
  activeKey,
  onToggle,
  onToggleAll,
  onSelectImage,
  onRegenerate,
}: FailedSectionProps) {
  const { t } = useTranslation();

  if (failed.length === 0) return null;

  const regenerable = failed.filter((i) => i.regenerable);
  const allSelected = regenerable.length > 0 && regenerable.every((i) => selected.has(imageKey(i)));
  const someSelected = regenerable.some((i) => selected.has(imageKey(i)));
  const selectedCount = regenerable.filter((i) => selected.has(imageKey(i))).length;

  return (
    <Box className="failed-section" component="section" aria-label={t("review.failed.title", { count: failed.length })}>
      <Group className="failed-section__header" justify="space-between" wrap="nowrap">
        <Group gap={spacing.xs} wrap="nowrap">
          <IconAlertTriangle size={16} className="failed-section__title-icon" aria-hidden="true" />
          <Text fw={500} size="sm" c="dimmed">
            {t("review.failed.title", { count: failed.length })}
          </Text>
        </Group>
        {canRegenerate && regenerable.length > 0 && (
          <Checkbox
            size="xs"
            checked={allSelected}
            indeterminate={!allSelected && someSelected}
            onChange={onToggleAll}
            label={t("review.failed.selectAll")}
            aria-label={t("review.failed.selectAll")}
          />
        )}
      </Group>

      <ScrollArea className="failed-section__list" type="auto">
        {failed.map((image) => {
          const key = imageKey(image);
          const active = key === activeKey;
          return (
            <div
              key={key}
              className={`failed-section__item${active ? " failed-section__item--active" : ""}`}
            >
              {canRegenerate && image.regenerable ? (
                <Checkbox
                  size="xs"
                  checked={selected.has(key)}
                  onChange={() => onToggle(key)}
                  aria-label={t("review.failed.selectImage", { objId: image.objId })}
                />
              ) : (
                <span className="failed-section__checkbox-spacer" aria-hidden="true" />
              )}
              <UnstyledButton
                className="failed-section__row"
                onClick={() => onSelectImage(image)}
                aria-current={active ? "true" : undefined}
              >
                <Text size="sm" truncate className="failed-section__label">
                  {t("review.failed.imageLabel", { objId: image.objId })}
                </Text>
                <Badge
                  color={image.regenerable ? "yellow" : "gray"}
                  variant="light"
                  size="sm"
                >
                  {t(`review.failed.reason.${image.reason ?? "generation_failed"}`, {
                    defaultValue: image.reason ?? t("review.failed.reason.generation_failed"),
                  })}
                </Badge>
              </UnstyledButton>
            </div>
          );
        })}
      </ScrollArea>

      {canRegenerate && regenerable.length > 0 && (
        <Box className="failed-section__footer">
          <Button
            fullWidth
            size="xs"
            leftSection={<IconRefresh size={14} />}
            disabled={selectedCount === 0 || !runParked}
            loading={submitting}
            onClick={onRegenerate}
          >
            {t("review.failed.regenerateButton", { count: selectedCount })}
          </Button>
        </Box>
      )}
    </Box>
  );
}
